import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api from "../lib/api";
import { useAuth } from "./AuthContext";

const CartContext = createContext(null);
const LS_KEY = "st_guest_cart";

const loadGuest = () => {
    try { return JSON.parse(localStorage.getItem(LS_KEY)) || { items: [] }; } catch { return { items: [] }; }
};
const saveGuest = (cart) => localStorage.setItem(LS_KEY, JSON.stringify({ items: cart.items || [] }));

const calcLocal = (cart) => {
    const subtotal = (cart.items || []).reduce((s, it) => s + (it.unit_price * it.quantity), 0);
    return { ...cart, subtotal: Number(subtotal.toFixed(2)), user_discount_amount: 0, user_discount_percent: 0, total: Number(subtotal.toFixed(2)) };
};

export const CartProvider = ({ children }) => {
    const { user } = useAuth();
    const [cart, setCart] = useState({ items: [], subtotal: 0, total: 0, user_discount_amount: 0 });
    const [open, setOpen] = useState(false);
    const mergedRef = useRef(false);

    const refresh = useCallback(async (u) => {
        const currentUser = u !== undefined ? u : user;
        if (currentUser) {
            try {
                const res = await api.get("/cart");
                setCart(res.data);
            } catch {}
        } else {
            setCart(calcLocal(loadGuest()));
        }
    }, [user]);

    // Single effect on user change - merge guest cart then refresh
    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (user) {
                if (!mergedRef.current) {
                    const guest = loadGuest();
                    if (guest.items?.length) {
                        for (const it of guest.items) {
                            try { await api.post("/cart/items", it); } catch {}
                        }
                        localStorage.removeItem(LS_KEY);
                    }
                    mergedRef.current = true;
                }
                if (!cancelled) {
                    try { const res = await api.get("/cart"); setCart(res.data); } catch {}
                }
            } else {
                mergedRef.current = false;
                if (!cancelled) setCart(calcLocal(loadGuest()));
            }
        })();
        return () => { cancelled = true; };
    }, [user]);

    const addItem = async (item) => {
        if (user) {
            const res = await api.post("/cart/items", item);
            setCart(res.data);
            setOpen(true);
            return;
        }
        const current = loadGuest();
        const idx = current.items.findIndex(x => x.product_id === item.product_id && x.package_id === item.package_id);
        if (idx >= 0) current.items[idx].quantity += item.quantity;
        else current.items.push(item);
        saveGuest(current);
        setCart(calcLocal(current));
        setOpen(true);
    };

    const updateQty = async (product_id, package_id, quantity) => {
        if (user) {
            const res = await api.put("/cart/items", { product_id, package_id, quantity });
            setCart(res.data);
            return;
        }
        const current = loadGuest();
        for (const it of current.items) {
            if (it.product_id === product_id && it.package_id === package_id) it.quantity = Math.max(0, quantity);
        }
        current.items = current.items.filter(it => it.quantity > 0);
        saveGuest(current);
        setCart(calcLocal(current));
    };

    const removeItem = async (product_id, package_id) => {
        if (user) {
            const res = await api.delete("/cart/items", { params: { product_id, package_id } });
            setCart(res.data);
            return;
        }
        const current = loadGuest();
        current.items = current.items.filter(it => !(it.product_id === product_id && it.package_id === package_id));
        saveGuest(current);
        setCart(calcLocal(current));
    };

    const clear = async () => {
        if (user) {
            try { await api.delete("/cart"); } catch {}
        } else {
            localStorage.removeItem(LS_KEY);
        }
        setCart({ items: [], subtotal: 0, total: 0, user_discount_amount: 0 });
    };

    const itemCount = (cart.items || []).reduce((s, it) => s + (it.quantity || 0), 0);

    return (
        <CartContext.Provider value={{ cart, refresh, addItem, updateQty, removeItem, clear, open, setOpen, itemCount, isGuest: !user }}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => useContext(CartContext);
